/**
 * WorkshopLiveView — HDMI-vy under pågående workshopsession.
 * Visar aktuell fråga, gruppernas svarsstatus och en synkad nedräkningstimer.
 * Lyssnar på display.timer.endsAt → räknar ner lokalt, inga rundresandeuppdateringar.
 * Kräver ingen inloggning.
 */
import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { db } from '../../../firebase/config';
import { doc, onSnapshot, collection } from 'firebase/firestore';
import { useCompanyLogo } from '../../../hooks/useQRLogo';

const accent = '#E05500';

function pad(n) { return String(n).padStart(2, '0'); }
function fmt(s) { return `${pad(Math.floor(s / 60))}:${pad(s % 60)}`; }

function playAlarm() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [880, 1100, 880, 1100, 880].forEach((f, i) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.frequency.value = f; o.type = 'square';
      const t = ctx.currentTime + i * 0.22;
      g.gain.setValueAtTime(0.12, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
      o.start(t); o.stop(t + 0.2);
    });
  } catch {}
}

export default function WorkshopLiveView({ sessionId }) {
  const [session,   setSession]   = useState(null);
  const [responses, setResponses] = useState([]);
  const [remaining, setRemaining] = useState(null); // seconds, null = no timer
  const [totalSecs, setTotalSecs] = useState(null);
  const [timerLabel, setTimerLabel] = useState('');
  const [loading,   setLoading]   = useState(true);
  const intervalRef = useRef(null);
  const alarmedRef  = useRef(false);
  const companyLogo = useCompanyLogo();

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'workshops', sessionId), snap => {
      setSession(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      setLoading(false);
    });
    return unsub;
  }, [sessionId]);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'workshops', sessionId, 'responses'), snap => {
      setResponses(snap.docs.map(d => ({ docId: d.id, ...d.data() })));
    });
    return unsub;
  }, [sessionId]);

  // Sync timer from Firestore display.timer
  useEffect(() => {
    const timer = session?.display?.timer;
    clearInterval(intervalRef.current);
    alarmedRef.current = false;

    if (!timer?.endsAt) {
      setRemaining(null);
      setTotalSecs(null);
      return;
    }

    setTotalSecs(timer.totalSeconds || 300);
    setTimerLabel(timer.label || '');

    const tick = () => {
      const secs = Math.max(0, Math.round((timer.endsAt - Date.now()) / 1000));
      setRemaining(secs);
      if (secs === 0 && !alarmedRef.current) {
        alarmedRef.current = true;
        playAlarm();
        clearInterval(intervalRef.current);
      }
    };
    tick();
    intervalRef.current = setInterval(tick, 500);
    return () => clearInterval(intervalRef.current);
  }, [session?.display?.timer]);

  if (loading) return (
    <Box sx={{ minHeight: '100vh', height: '100%', bgcolor: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress sx={{ color: accent }} />
    </Box>
  );

  const questions = session?.config?.groups ? session.config.questions || [] : [];
  const groups    = session?.config?.groups || [];
  const currentQ  = session?.status?.currentQuestion ?? 0;
  const question  = questions[currentQ];

  const hasTimer = remaining !== null;
  const fraction = (hasTimer && totalSecs > 0) ? remaining / totalSecs : 0;
  const timerDone = remaining === 0;
  const ringColor = timerDone ? '#ef4444' : remaining <= 60 ? '#ef4444' : remaining <= 120 ? '#FF9800' : accent;

  // SVG ring
  const R = 110, CIRC = 2 * Math.PI * R;

  return (
    <Box sx={{
      minHeight: '100vh', height: '100%',
      bgcolor: '#1a1a1a',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      px: { xs: 4, md: 8 }, py: { xs: 4, md: 6 },
      gap: { xs: 3, md: 5 },
      userSelect: 'none',
      position: 'relative',
    }}>

      {/* Question */}
      {question && (
        <Box sx={{ textAlign: 'center', maxWidth: '75vw' }}>
          <Typography sx={{
            color: '#555', fontSize: 'clamp(0.55rem, 1vw, 0.75rem)',
            fontWeight: 700, letterSpacing: 3, mb: 1.5, textTransform: 'uppercase',
          }}>
            Fråga {currentQ + 1} / {questions.length}
          </Typography>
          <Typography sx={{
            color: '#fff',
            fontSize: 'clamp(1.2rem, 3vw, 2.6rem)',
            fontWeight: 500, lineHeight: 1.3,
          }}>
            {question.text}
          </Typography>
        </Box>
      )}

      {/* Timer ring */}
      {hasTimer && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width={260} height={260} style={{ transform: 'rotate(-90deg)' }}>
              <circle cx={130} cy={130} r={R} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={8} />
              <circle cx={130} cy={130} r={R} fill="none"
                stroke={ringColor} strokeWidth={8} strokeLinecap="round"
                strokeDasharray={`${CIRC * fraction} ${CIRC}`}
                style={{ transition: 'stroke-dasharray 0.8s linear, stroke 0.3s' }}
              />
            </svg>
            <Box sx={{ position: 'absolute', textAlign: 'center' }}>
              <Typography sx={{
                fontSize: 'clamp(2.5rem, 5vw, 4rem)',
                fontWeight: 900, color: timerDone ? '#ef4444' : '#fff',
                lineHeight: 1, fontVariantNumeric: 'tabular-nums',
                letterSpacing: -2, transition: 'color 0.3s',
              }}>
                {fmt(remaining)}
              </Typography>
              {timerDone && (
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#ef4444', letterSpacing: 2, mt: 0.5 }}>
                  TIDEN ÄR UTE
                </Typography>
              )}
            </Box>
          </Box>
          {timerLabel && (
            <Typography sx={{ color: '#444', fontSize: 'clamp(0.65rem, 1.2vw, 0.9rem)', letterSpacing: 1 }}>
              {timerLabel}
            </Typography>
          )}
        </Box>
      )}

      {/* Group answer status */}
      {groups.length > 0 && (
        <Box sx={{ display: 'flex', gap: { xs: 1.5, md: 2.5 }, flexWrap: 'wrap', justifyContent: 'center' }}>
          {groups.map(g => {
            const count = responses.filter(
              r => r.groupId === g.id && r.answers?.[currentQ] !== undefined && r.answers?.[currentQ] !== ''
            ).length;
            const hasAnswers = count > 0;
            return (
              <Box key={g.id} sx={{
                px: { xs: 1.5, md: 2 }, py: { xs: 0.5, md: 0.75 },
                borderRadius: 1.5,
                bgcolor: hasAnswers ? (g.color || accent) + '20' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${hasAnswers ? (g.color || accent) + '55' : 'rgba(255,255,255,0.07)'}`,
                display: 'flex', alignItems: 'center', gap: 1,
                transition: 'all 0.4s',
              }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: hasAnswers ? (g.color || accent) : '#333', transition: 'background-color 0.4s' }} />
                <Typography sx={{ color: hasAnswers ? '#ccc' : '#333', fontSize: 'clamp(0.7rem, 1.5vw, 1rem)', fontWeight: 600, transition: 'color 0.4s' }}>
                  {g.name}
                </Typography>
                {hasAnswers && (
                  <Typography sx={{ color: '#555', fontSize: 'clamp(0.6rem, 1vw, 0.75rem)' }}>
                    {count} svar
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Logo */}
      {companyLogo && (
        <Box component="img" src={companyLogo} alt="" sx={{
          position: 'fixed', bottom: { xs: 16, md: 28 }, right: { xs: 16, md: 32 },
          height: { xs: 22, md: 32 }, maxWidth: 140,
          objectFit: 'contain', opacity: 0.35,
        }} />
      )}
    </Box>
  );
}
